import type { GridStackNode } from "gridstack";
import type { InjectionKey } from "vue";
import { MetricPeriodType } from "~/graphql/generated/graphql";
import type { QueryState } from "~/helpers/apollo/types";
import type { DateRangeConfig } from "~/helpers/user-preferences-helpers";

export namespace FrontpageWidget {
  export interface ConfigBase {
    baseType: "frontpageWidget_1";
    moduleType: ConfigModuleType;
    configType: ConfigType;
  }

  export namespace Opex {
    export interface ConfigBase extends FrontpageWidget.ConfigBase {
      moduleType: "opex";
      templateId: string;
      title?: string;
    }

    export interface Total extends ConfigBase {
      configType: "total_1";
    }

    export interface List extends ConfigBase {
      configType: "list_1";
      compact: boolean;
    }

    const getDefault = <T extends Config>(
      configType: T["configType"],
      { title, compact = false, templateId = "normLatest12" }: { title?: string; compact?: boolean; templateId?: string } = {}
    ) =>
      ({
        baseType: "frontpageWidget_1",
        moduleType: "opex",
        configType,
        templateId,
        title,
        compact,
      } as T);

    export const getListDefault = (args?: Parameters<typeof getDefault>[1]): List => getDefault("list_1", args);
    export const getTotalDefault = (args?: Parameters<typeof getDefault>[1]): Total => getDefault("total_1", args);

    export type Config = Total | List;
  }

  export namespace Accounts {
    export interface ConfigBase extends FrontpageWidget.ConfigBase {
      moduleType: "accounts";
      categoryIds: string[];
      propertyId?: string;
      portfolioId?: string;
      dateConfig: DateRangeConfig;
      title: string;
    }

    export interface LatestRealized extends FrontpageWidget.Accounts.ConfigBase {
      configType: "latestRealized_1";
    }

    export interface Realized extends FrontpageWidget.Accounts.ConfigBase {
      configType: "realized_1";
    }

    const getDefault = <T extends Config>(
      configType: T["configType"],
      {
        title = configType === "realized_1" ? "Realized" : "Latest Realized",
        dateConfig = { customDateEnd: undefined, customDateStart: undefined, dateRange: "latest12", periodType: MetricPeriodType.Monthly },
        portfolioId,
        propertyId,
      }: { dateConfig?: DateRangeConfig; portfolioId?: Nullable<string>; propertyId?: Nullable<string>; title?: string } = {}
    ): T =>
      ({
        baseType: "frontpageWidget_1",
        moduleType: "accounts",
        configType,
        categoryIds: <string[]>[],
        dateConfig,
        portfolioId: portfolioId ?? undefined,
        propertyId: propertyId ?? undefined,
        title,
      } as T);

    export const getRealizedDefault = (args?: Parameters<typeof getDefault>[1]): Realized => getDefault("realized_1", args);
    export const getLatestRealizedDefault = (args?: Parameters<typeof getDefault>[1]): LatestRealized => getDefault("latestRealized_1", args);

    export type Config = LatestRealized | Realized;
  }

  export namespace Rent {
    interface PlannedRent extends ConfigBase {
      moduleType: "rent";
      configType: "plannedRent_1";
      propertyId?: string;
      portfolioId?: string;
      dateConfig: DateRangeConfig;
      title: string;
    }

    export const getDefault = ({
      title = "Planned Rent",
      dateConfig = { customDateEnd: undefined, customDateStart: undefined, dateRange: "latest12", periodType: MetricPeriodType.Monthly },
      portfolioId,
      propertyId,
    }: {
      dateConfig?: DateRangeConfig;
      portfolioId?: Nullable<string>;
      propertyId?: Nullable<string>;
      title?: string;
    } = {}): PlannedRent =>
      ({
        configType: "plannedRent_1",
        baseType: "frontpageWidget_1",
        moduleType: "rent",
        propertyId: propertyId ?? undefined,
        portfolioId: portfolioId ?? undefined,
        dateConfig,
        title,
      } as PlannedRent);

    export type Config = PlannedRent;
  }

  export namespace Portfolio {
    interface Portfolio extends ConfigBase {
      configType: "portfolio_1";
      portfolioId: string;
    }

    export type Config = Portfolio;
  }

  export namespace Count {
    interface Count extends ConfigBase {
      configType: "count_1";
      type: "portfolio" | "property" | "tenancy";
    }

    export type Config = Count;
  }

  export namespace Text {
    interface Text extends ConfigBase {
      configType: "text_1";
      type: "text";
      text: string;
    }

    export type Config = Text;
  }

  export type ConfigType =
    | FrontpageWidget.Opex.Total["configType"]
    | FrontpageWidget.Opex.List["configType"]
    | FrontpageWidget.Accounts.LatestRealized["configType"]
    | FrontpageWidget.Accounts.Realized["configType"]
    | FrontpageWidget.Rent.Config["configType"]
    | FrontpageWidget.Portfolio.Config["configType"]
    | FrontpageWidget.Count.Config["configType"]
    | FrontpageWidget.Text.Config["configType"];

  export type ConfigModuleType =
    | FrontpageWidget.Opex.ConfigBase["moduleType"]
    | FrontpageWidget.Accounts.ConfigBase["moduleType"]
    | FrontpageWidget.Rent.Config["moduleType"];

  export type Config = Opex.Config | Accounts.Config | Portfolio.Config | Count.Config;

  export type UnitConfig = { type: "currency"; value: string } | { type: "metric"; value: string } | { type: "plain"; value: string };
}

export const isFrontpageWidgetConfig = (config: Object): config is FrontpageWidget.Config => "baseType" in config && config.baseType === "frontpageWidget_1";

export interface WidgetWithType extends GridStackNode {
  type: WidgetType;
}

export const QueryStateInjectionKey = Symbol("queryState") as InjectionKey<Ref<QueryState>>;
